import { createSlice } from "@reduxjs/toolkit";

/**
 * @typedef {Object} ArticuloControlado
 * @property {number} idArticulo
 * @property {string} descripcion
 * @property {number} stockReal
 * @property {number|null} stockTeorico
 * @property {string} [codigo]
 * @property {string} [codigoBarra]
 * @property {string} [ubicacion]
 */

/**
 * @typedef {Object} ArticuloBasico
 * @property {number} idArticulo
 * @property {string} descripcion
 */

/**
 * @typedef {Object} ArticuloIMAPorId
 * @property {number} idArticulo
 * @property {string} descripcion
 * @property {number|null} stockTeorico
 * @property {string} [codigo]
 * @property {string} [codigoBarra]
 */

/**
 * Estado inicial del store de control de stock
 */
const initialState = {
  // ===== Estados generales =====
  codigoBarra: "",
  articulo: null,
  stockTeorico: null,
  stockReal: null,
  msgError: "",

  // ===== Artículos disponibles =====
  articulosDisponibles: [],

  // ===== Artículo actual para control al azar / selectivo =====
  articuloActual: null,

  // ===== Diálogos de confirmación =====
  cancelarDialogOpen: false,
  proximoDialogOpen: false,
  finalizarDialogOpen: false,

  // ===== Lista de artículos controlados =====
  articulosControlados: [],

  // ===== Comprobante final =====
  mensajeComprobante: "",
};

/**
 * Slice principal para el control de stock
 * Maneja todos los estados relacionados con el control de inventario
 */
const stockControlSlice = createSlice({
  name: "stockControl",
  initialState,
  reducers: {
    // ===== Setters generales =====
    setCodigoBarra: (state, action) => {
      state.codigoBarra = action.payload;
    },
    setArticulo: (state, action) => {
      state.articulo = action.payload;
    },
    setStockTeorico: (state, action) => {
      state.stockTeorico = action.payload;
    },
    setStockReal: (state, action) => {
      state.stockReal = action.payload;
    },
    setMsgError: (state, action) => {
      state.msgError = action.payload;
    },

    // ===== Artículos disponibles =====
    setArticulosDisponibles: (state, action) => {
      state.articulosDisponibles = action.payload;
    },

    // ===== Artículo actual para control al azar / selectivo =====
    setArticuloActual: (state, action) => {
      state.articuloActual = action.payload;
    },
    avanzarArticuloAlAzar: state => {
      state.articuloActual = null;
      state.stockReal = null;
    },

    // ===== Reset del formulario =====
    resetFormulario: state => {
      state.codigoBarra = "";
      state.articulo = null;
      state.stockTeorico = null;
      state.stockReal = null;
      state.msgError = "";
    },

    // ===== Diálogos de confirmación =====
    // Diálogo cancelar
    openCancelarDialog: state => {
      state.cancelarDialogOpen = true;
    },
    closeCancelarDialog: state => {
      state.cancelarDialogOpen = false;
    },

    // Diálogo próximo
    openProximoDialog: state => {
      state.proximoDialogOpen = true;
    },
    closeProximoDialog: state => {
      state.proximoDialogOpen = false;
    },

    // Diálogo finalizar
    openFinalizarDialog: state => {
      state.finalizarDialogOpen = true;
    },
    closeFinalizarDialog: state => {
      state.finalizarDialogOpen = false;
    },

    // ===== Lista de artículos controlados =====
    agregarArticuloControlado: (state, action) => {
      state.articulosControlados.push(action.payload);
      console.log("📦 Artículos actualizados:", state.articulosControlados);
    },
    resetArticulosControlados: state => {
      state.articulosControlados = [];
    },

    // ===== Comprobante final =====
    setMensajeComprobante: (state, action) => {
      state.mensajeComprobante = action.payload;
    },
    resetMensajeComprobante: state => {
      state.mensajeComprobante = "";
    },

    // ===== Resetea todo el estado del store =====
    resetTodo: state => {
      Object.assign(state, initialState);
    },

    // ===== Acciones complejas =====
    /**
     * Guarda un artículo controlado y resetea el formulario
     */
    guardarYResetear: (state, action) => {
      state.articulosControlados.push(action.payload);
      state.codigoBarra = "";
      state.articulo = null;
      state.stockTeorico = null;
      state.stockReal = null;
      state.msgError = "";
      state.articuloActual = null;
    },

    /**
     * Marca un artículo como inexistente (stock = 0)
     */
    marcarInexistente: (state, action) => {
      const articuloInexistente = { ...action.payload, stockReal: 0 };
      state.articulosControlados.push(articuloInexistente);
      state.codigoBarra = "";
      state.articulo = null;
      state.stockTeorico = null;
      state.stockReal = null;
      state.msgError = "";
      state.articuloActual = null;
    },
  },
});

// ===== Exportar acciones =====
export const {
  setCodigoBarra,
  setArticulo,
  setStockTeorico,
  setStockReal,
  setMsgError,
  setArticulosDisponibles,
  setArticuloActual,
  avanzarArticuloAlAzar,
  resetFormulario,
  openCancelarDialog,
  closeCancelarDialog,
  openProximoDialog,
  closeProximoDialog,
  openFinalizarDialog,
  closeFinalizarDialog,
  agregarArticuloControlado,
  resetArticulosControlados,
  setMensajeComprobante,
  resetMensajeComprobante,
  resetTodo,
  guardarYResetear,
  marcarInexistente,
} = stockControlSlice.actions;

// ===== Selectores =====
/**
 * Selectores básicos
 */
export const selectCodigoBarra = state => state.stockControl.codigoBarra;
export const selectArticulo = state => state.stockControl.articulo;
export const selectStockTeorico = state => state.stockControl.stockTeorico;
export const selectStockReal = state => state.stockControl.stockReal;
export const selectMsgError = state => state.stockControl.msgError;
export const selectArticulosDisponibles = state => state.stockControl.articulosDisponibles;
export const selectArticuloActual = state => state.stockControl.articuloActual;
export const selectArticulosControlados = state => state.stockControl.articulosControlados;
export const selectMensajeComprobante = state => state.stockControl.mensajeComprobante;

/**
 * Selectores de diálogos
 */
export const selectCancelarDialogOpen = state => state.stockControl.cancelarDialogOpen;
export const selectProximoDialogOpen = state => state.stockControl.proximoDialogOpen;
export const selectFinalizarDialogOpen = state => state.stockControl.finalizarDialogOpen;

/**
 * Selector para obtener el estado de validación del formulario
 */
export const selectFormValidation = state => ({
  isValid: state.stockControl.stockReal !== null && !isNaN(state.stockControl.stockReal),
  hasError: state.stockControl.msgError !== "",
  canSubmit: state.stockControl.stockReal !== null && !isNaN(state.stockControl.stockReal) && state.stockControl.msgError === "",
});

/**
 * Selector para obtener estadísticas del control
 */
export const selectControlStats = state => {
  const total = state.stockControl.articulosControlados.length;
  const conDiferencias = state.stockControl.articulosControlados.filter(a => a.stockReal !== a.stockTeorico).length;
  const sinDiferencias = total - conDiferencias;

  return {
    total,
    conDiferencias,
    sinDiferencias,
    porcentajeExacto: total > 0 ? Math.round((sinDiferencias / total) * 100) : 0,
  };
};

/**
 * Selector para obtener el estado de los diálogos
 */
export const selectDialogState = state => ({
  cancelarOpen: state.stockControl.cancelarDialogOpen,
  proximoOpen: state.stockControl.proximoDialogOpen,
  finalizarOpen: state.stockControl.finalizarDialogOpen,
  anyDialogOpen: state.stockControl.cancelarDialogOpen || state.stockControl.proximoDialogOpen || state.stockControl.finalizarDialogOpen,
});

/**
 * Selector para obtener el total de artículos controlados
 */
export const selectTotalArticulosControlados = state => state.stockControl.articulosControlados.length;

/**
 * Selector para verificar si hay diferencias en el stock
 */
export const selectHayDiferenciasStock = state => state.stockControl.articulosControlados.some(articulo => articulo.stockReal !== articulo.stockTeorico);

/**
 * Selector para obtener solo los artículos con diferencias
 */
export const selectArticulosConDiferencias = state => state.stockControl.articulosControlados.filter(articulo => articulo.stockReal !== articulo.stockTeorico);

/**
 * Selector para buscar un artículo por código
 */
export const selectArticuloPorCodigo = (state, codigo) =>
  state.stockControl.articulosDisponibles.find(a => a.codigo === codigo || a.codigoBarra === codigo) || null;

// ===== Exportar reducer =====
export default stockControlSlice.reducer;
